// src/lib/handoffs.js
// STATUS: active | UPDATED: 2026-09-10
// LOG:
//   2026-09-10  New. The first thing in this product owned by two people.
// Logging and confirming the steps that cannot be done alone.
//
// Steps 3, 7 and 11 each need a second person. This is the record that one
// happened, and it is the seed of every team number the product will ever show,
// because it is the only measure here that produces no last place.
//
// NEVER PUT CUSTOMER DATA IN THESE ROWS. Not a name, not an initial, not a
// unit, not a deal. The table has nowhere to accept it and this file must never
// grow a field that does. That rule is the one with legal teeth in this repo.

import { supabase } from './supabase'

export const HANDOFF_KINDS = {
  3: { kind: 'manager_intro', label: 'Manager introduction' },
  7: { kind: 'service_tour', label: 'Service and parts introduction' },
  11: { kind: 'finance', label: 'Finance handoff' },
}

// Log that it happened. `briefed` is the half Jazz says is the actual point:
// the salesperson steps out and briefs the manager BEFORE bringing them in.
export async function logHandoff({ stepNumber, userId, dealershipId, briefed, counterpartId = null }) {
  const spec = HANDOFF_KINDS[stepNumber]
  if (!spec || !userId || !dealershipId) return { error: 'missing required fields' }
  const { data, error } = await supabase
    .from('handoffs')
    .insert({
      dealership_id: dealershipId,
      salesperson_id: userId,
      counterpart_id: counterpartId,
      kind: spec.kind,
      step_number: stepNumber,
      briefed: Boolean(briefed),
    })
    .select('id')
    .single()
  return { data, error }
}

// It did NOT happen, and why. Recorded rather than left absent, because an
// empty table cannot tell you whether nothing happened or nobody logged it, and
// "there was no manager on the floor" is the most useful row here.
export async function logBlockedHandoff({ stepNumber, userId, dealershipId, reason }) {
  const spec = HANDOFF_KINDS[stepNumber]
  if (!spec || !userId || !dealershipId) return { error: 'missing required fields' }
  const { data, error } = await supabase
    .from('handoffs')
    .insert({
      dealership_id: dealershipId,
      salesperson_id: userId,
      kind: spec.kind,
      step_number: stepNumber,
      briefed: false,
      blocked_reason: String(reason || '').slice(0, 300),
    })
    .select('id')
    .single()
  return { data, error }
}

// The other person says yes, this happened. RLS decides who may: the named
// counterpart, anyone who manages that salesperson, or Level 00.
export async function confirmHandoff(handoffId, confirmerId) {
  const { error } = await supabase
    .from('handoffs')
    .update({ confirmed_at: new Date().toISOString(), confirmed_by: confirmerId })
    .eq('id', handoffId)
  return { error }
}

// What this person logged today, so the UI can say "already logged" instead of
// inviting a second one for the same customer.
export async function handoffsToday(userId, stepNumber) {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const { data, error } = await supabase
    .from('handoffs')
    .select('id, briefed, blocked_reason, confirmed_at, occurred_at')
    .eq('salesperson_id', userId)
    .eq('step_number', stepNumber)
    .gte('occurred_at', start.toISOString())
    .order('occurred_at', { ascending: false })
  return { data: data || [], error }
}

// Waiting on this person to confirm. Drives the manager's side.
export async function handoffsAwaitingMe(userId, dealershipId) {
  if (!dealershipId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('handoffs')
    .select('id, kind, step_number, briefed, occurred_at, salesperson_id')
    .eq('dealership_id', dealershipId)
    .is('confirmed_at', null)
    .is('blocked_reason', null)
    .order('occurred_at', { ascending: false })
    .limit(20)
  return { data: data || [], error }
}
